import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type {
  CancellationToken,
  FailedMeasure,
  QueryLoggingItem,
  RunTileReportRequest,
  TileDataResponse,
} from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";

type CancellableTileRequest = {
  cancel: () => void;
};

interface Props {
  requestConfig: RunTileReportRequest;
  runReport: (
    dataSetId: string,
    body: RunTileReportRequest,
    done: (data: ApiResponse<TileDataResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  updateDevTools?: (loggingItems: QueryLoggingItem[] | undefined) => void;
  setEtag?: (etag: string | undefined, cacheValid: boolean | undefined) => void;
}
const useDataLoadingViewer = ({ updateDevTools, setEtag, requestConfig, runReport }: Props) => {
  const { dataSetId } = useBiCommonDataContext();

  const [cancellationToken, setCancellationToken] = useState<CancellableTileRequest | undefined>(undefined);
  const [error, setError] = useState<string>();
  const [isCalculating, setIsCalculating] = useState(false);
  const [failedMeasures, setFailedMeasures] = useState<FailedMeasure[]>([]);
  const [data, setData] = useState<TileDataResponse>();
  const [initialized, setInitialized] = useState(false);

  const buildConfigRef = useRef(requestConfig);
  const cancellationTokenRef = useRef<CancellableTileRequest | undefined>(cancellationToken);
  useEffect(() => {
    buildConfigRef.current = requestConfig;
  }, [requestConfig]);
  useEffect(() => {
    cancellationTokenRef.current = cancellationToken;
  }, [cancellationToken]);

  useEffect(() => {
    return () => {
      cancellationTokenRef.current?.cancel();
    };
  }, []);

  const handleBuildDone = useCallback(
    (response: ApiResponse<TileDataResponse>) => {
      if (response.success) {
        if (response.data.errorCode) {
          setError(response.data.errorCode);
        } else {
          setError(undefined);
          setData(response.data);
          updateDevTools?.(response.data.loggingItems);
          setEtag?.(response.data.etag, undefined);
          setFailedMeasures(response.data.failedMeasures);
        }
      } else if (response.error?.message) {
        setError(response.error.message);
      }
      setIsCalculating(false);
      setInitialized(true);
    },
    [updateDevTools, setEtag]
  );

  const handleBuildError = useCallback((reason: string | undefined) => {
    if (reason === "canceled") {
      setError(undefined);
    } else {
      setError(reason || "An error occurred while calculating report data. Please try again.");
    }
    setIsCalculating(false);
    setInitialized(true);
  }, []);

  const calculate = useCallback(() => {
    setIsCalculating(true);
    setError(undefined);
    setEtag?.(undefined, undefined);
    setCancellationToken(runReport(dataSetId, buildConfigRef.current, handleBuildDone, handleBuildError));
  }, [runReport, dataSetId, setEtag, handleBuildDone, handleBuildError]);

  return {
    data,
    error,
    isCalculating,
    failedMeasures,
    calculate,
    initialized,
  };
};

export default useDataLoadingViewer;
